import {
	deleteRecordingChunksForSession,
	listRecordingChunks,
} from '@services/siteVisitRecording/siteVisitRecordingChunkStore';
import { removeSiteVisitRecordingSession } from '@services/siteVisitRecording/siteVisitRecordingPersistence';
import {
	finalizeSiteVisitRecording,
	isSiteVisitRecordingSessionActive,
} from '@services/siteVisitRecording/siteVisitRecordingEngine';
import {
	uploadSiteVisitRecording,
	SiteVisitRecordingUploadResponse,
} from '@services/siteVisitRecording/siteVisitRecordingApi';
import { assembleSiteVisitRecordingBlob } from '@services/siteVisitRecording/recordingMedia';
import type { PendingSiteVisitRecordingUpload } from '@store/siteVisitRecordingUploadStore';
import { useSiteVisitRecordingUploadStore } from '@store/siteVisitRecordingUploadStore';
import useSiteLeadStore from '@store/siteLeadStore';
import { ToastType, useTosterStore } from '@store/toster';

/** Prevents parallel upload attempts (siteAddress submit + VisitRecord success page + Strict Mode). */
let pendingUploadInFlight: Promise<SiteVisitRecordingUploadResponse> | null = null;

/**
 * Small IndexedDB chunks → merge into one audio blob → POST site-blob-presigned-url → PUT merged file.
 */
export async function uploadPendingSiteVisitRecording(
	pending: PendingSiteVisitRecordingUpload,
	showToast: boolean = false
): Promise<SiteVisitRecordingUploadResponse> {
	if (pendingUploadInFlight) {
		return pendingUploadInFlight;
	}

	pendingUploadInFlight = (async (): Promise<SiteVisitRecordingUploadResponse> => {
		try {
			const records = await listRecordingChunks(pending.clientSessionId);
			const mergedAudio =
				records.length > 0
					? assembleSiteVisitRecordingBlob(
							records.map((record) => record.blob),
							pending.mimeType
						)
					: pending.audio;

			// Prefer visit sfid returned by update_site_visit (not site sfid).
			const latestSfid = useSiteLeadStore.getState().visitSfid || pending.sfid || null;
			const response = await uploadSiteVisitRecording({
				...pending,
				sfid: latestSfid,
				audio: mergedAudio,
			});

			await deleteRecordingChunksForSession(pending.clientSessionId);
			await removeSiteVisitRecordingSession(pending.clientSessionId);

			if (showToast) {
				useTosterStore.getState().setToaster(true, ToastType.success, 'Visit recording uploaded successfully.', 5000);
			}

			return response;
		} catch (error) {
			if (showToast) {
				const msg = error instanceof Error ? error.message : 'Visit recording upload failed';
				useTosterStore.getState().setToaster(true, ToastType.error, msg, 5000);
			}
			throw error;
		} finally {
			pendingUploadInFlight = null;
		}
	})();

	return pendingUploadInFlight;
}

/** Prevents parallel stop+upload (siteAddress submit + VisitRecord success page + Strict Mode). */
let stopAndUploadInFlight: Promise<boolean> | null = null;

/**
 * Stops an in-progress visit recording (if the user did not tap Stop), then uploads the pending blob.
 * Used on site visit form / draft Submit.
 * @returns whether a recording was uploaded to the backend
 */
export async function stopActiveRecordingAndUploadPendingSiteVisit(showToast: boolean = false): Promise<boolean> {
	if (stopAndUploadInFlight) {
		return stopAndUploadInFlight;
	}

	stopAndUploadInFlight = (async (): Promise<boolean> => {
		try {
			if (isSiteVisitRecordingSessionActive()) {
				await finalizeSiteVisitRecording(false);
			}

			const pending = useSiteVisitRecordingUploadStore.getState().pending;
			if (!pending) {
				return false;
			}

			try {
				await uploadPendingSiteVisitRecording(pending, showToast);
				useSiteVisitRecordingUploadStore.getState().clearPendingRecording();
				return true;
			} catch {
				// Upload failed (e.g. GCS CORS); toast already shown — do not reject (background upload).
				return false;
			}
		} finally {
			stopAndUploadInFlight = null;
		}
	})();

	return stopAndUploadInFlight;
}
